import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, BarChart3, Shield } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="border-b border-[#4A2C2A]/10 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Rejuven" className="h-10 w-auto" />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-[#4A2C2A]/70 hover:text-[#4A2C2A] text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#4A2C2A]/10 flex items-center justify-center">
            <Cookie className="w-6 h-6 text-[#4A2C2A]" />
          </div>
          <div>
            <h1 className="text-3xl font-heading text-[#4A2C2A]">Cookie Policy</h1>
            <p className="text-[#4A2C2A]/60 text-sm">Last updated: January 28, 2026</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none text-[#4A2C2A]/80">
          <p className="lead text-lg">
            This Cookie Policy explains how Rejuven Inc. uses cookies and similar technologies to recognize you when you visit our platform. It explains what these technologies are and why we use them.
          </p>

          <h2 className="text-[#4A2C2A]">What Are Cookies?</h2>
          <p>
            Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners to make their websites work efficiently and to provide reporting information.
          </p>
          <p>
            Cookies set by the website owner (in this case, Rejuven Inc.) are called "first-party cookies." Cookies set by parties other than the website owner are called "third-party cookies."
          </p>

          <h2 className="flex items-center gap-2 text-[#4A2C2A]">
            <Settings className="w-5 h-5 text-[#8DA399]" />
            Types of Cookies We Use
          </h2>

          <div className="grid gap-4 my-6">
            <div className="bg-white rounded-xl p-5 border border-[#E8A095]/20">
              <h3 className="text-lg font-semibold text-[#4A2C2A] mt-0 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#8DA399]" />
                Essential Cookies
              </h3>
              <p className="text-sm text-[#4A2C2A]/70 mb-0">
                These cookies are strictly necessary for the website to function and cannot be switched off. They are usually set in response to actions you take, such as logging in, setting your language preferences, or filling in forms.
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#E8A095]/20">
              <h3 className="text-lg font-semibold text-[#4A2C2A] mt-0 mb-2 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#E8A095]" />
                Performance Cookies
              </h3>
              <p className="text-sm text-[#4A2C2A]/70 mb-0">
                These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#E8A095]/20">
              <h3 className="text-lg font-semibold text-[#4A2C2A] mt-0 mb-2 flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#4A2C2A]" />
                Functional Cookies
              </h3>
              <p className="text-sm text-[#4A2C2A]/70 mb-0">
                These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
              </p>
            </div>
          </div>

          <h2 className="text-[#4A2C2A]">Cookies We Use</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-[#4A2C2A]/20">
                  <th className="text-left py-3 px-4 text-[#4A2C2A] font-semibold">Cookie Name</th>
                  <th className="text-left py-3 px-4 text-[#4A2C2A] font-semibold">Purpose</th>
                  <th className="text-left py-3 px-4 text-[#4A2C2A] font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#4A2C2A]/10">
                  <td className="py-3 px-4 font-mono text-xs">rejuven_token</td>
                  <td className="py-3 px-4">Authentication and session management</td>
                  <td className="py-3 px-4">7 days</td>
                </tr>
                <tr className="border-b border-[#4A2C2A]/10">
                  <td className="py-3 px-4 font-mono text-xs">rejuven_language</td>
                  <td className="py-3 px-4">Stores your language preference</td>
                  <td className="py-3 px-4">1 year</td>
                </tr>
                <tr className="border-b border-[#4A2C2A]/10">
                  <td className="py-3 px-4 font-mono text-xs">rejuven_user</td>
                  <td className="py-3 px-4">Stores user profile information</td>
                  <td className="py-3 px-4">7 days</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-[#4A2C2A]">How to Control Cookies</h2>
          <p>
            You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of this website may become inaccessible or not function properly.
          </p>
          <p>
            Most web browsers allow some control of most cookies through the browser settings. To find out more about cookies, including how to see what cookies have been set, visit{' '}
            <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#E8A095] hover:text-[#D68C82]">
              www.aboutcookies.org
            </a>
          </p>

          <h2 className="text-[#4A2C2A]">Browser-Specific Instructions</h2>
          <ul>
            <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
            <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
            <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
            <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies</li>
          </ul>

          <h2 className="text-[#4A2C2A]">Updates to This Policy</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. Please revisit this Cookie Policy regularly to stay informed about our use of cookies.
          </p>

          <div className="bg-[#E8A095]/10 rounded-2xl p-6 my-8">
            <h3 className="text-[#4A2C2A] mt-0">Questions?</h3>
            <p className="mb-0">
              If you have any questions about our use of cookies, please contact us at{' '}
              <a href="mailto:privacy@rejuven.com" className="text-[#E8A095] hover:text-[#D68C82]">
                privacy@rejuven.com
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#4A2C2A]/10 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-[#4A2C2A]/60">
          © 2026 Rejuven Inc. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
